import React, { useState, useEffect, useRef } from "react";
import Header from "../../component/menu/Header";
import SideMenu from "../../component/menu/SideMenu";
import useUserStore from "../../stores/store";
import OrderReportArchivedComponent from "../../component/restaurants/reports/OrderReportArchivedComponent";
import { ReportService } from "../../services/reports-services";
import ApiService from "../../utils/ApiService";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { message } from "antd";
import CustomLoader from "../../component/menu/CustomLoader";

export default function OrdersReportArchived() {
  dayjs.extend(isSameOrBefore);

  const { user, restaurantId, showNotification, networkCall, setNetworkCall } = useUserStore();
  const [ordersReportList, setOrdersReportList] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItem, setTotalItem] = useState(0);
  const [ordersFilter, setOrdersFilter] = useState({
    restaurantId: restaurantId,
    fromDateTime: "",
    toDateTime: "",
  });
  const [isFilterClear, setIsFilterClear] = useState(false);
  const isFetching = useRef(false);
  // const [isLoading, setIsLoading] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [fromDateTime, setFromDateTime] = useState("");
  const [toDateTime, setToDateTime] = useState("");


  useEffect(() => {
    setOrdersFilter((prev) => ({
      ...prev,
      fromDateTime,
      toDateTime,
      page,
      perPage,
    }));
  }, [fromDateTime, toDateTime, page, perPage]);

  async function fetchOrdersReportList(filter) {
    setNetworkCall(true);
    try {
      const orders = await ReportService.getOrderReportList(
        filter,
        user.token.token
      );
      setOrdersReportList(ReportService.formatDataList(orders.data));
      setPage(orders.page);
      setPerPage(orders.perPage);
      setTotalItem(orders.total);
      setNetworkCall(false);
    } catch (err) {
      showNotification("Something went wrong in serverside. Try again later.");
      setNetworkCall(false);
      return;
    }
  }

  useEffect(() => {
    async function fetchData() {
      if (isFetching.current) return;
      isFetching.current = true;

      try {
        const tempFilter = { ...ordersFilter, page, perPage };
        await fetchOrdersReportList(tempFilter);
      } catch (err) {
        console.error("Error fetching:", err);
      } finally {
        isFetching.current = false;
      }
    }

    fetchData();
  }, [page, perPage]);

  useEffect(() => {
    async function fetchData() {
      if (isFilterClear) {
        await fetchOrdersReportList(ordersFilter);
        setIsFilterClear(false);
      }
    }

    fetchData();
  }, [isFilterClear]);

  function onHandleFilterData() {
    const { fromDateTime, toDateTime } = ordersFilter;

    if (!Boolean(fromDateTime) || !Boolean(toDateTime)) {
      showNotification("Please select from and to date first!");
      return;
    }

    if (page !== 1 || perPage !== 10) {
      setPage(1);
      setPerPage(10);
    }
    const tempFilter = { ...ordersFilter, page: 1, perPage: 10 };
    fetchOrdersReportList(tempFilter);
  }

  function onHandleFromDate(date, dateString) {
    if (!date || !dateString) {
      setFromDateTime(null);
      return;
    }

    const tempFromDate = date.format(`YYYY-MM-DD`);
    const threeDaysBefore = dayjs().subtract(3, "day").endOf("day");

    if (!tempFromDate || !dayjs(tempFromDate).isValid()) return;

    if (dayjs(tempFromDate).isAfter(threeDaysBefore)) {
      message.warning("You cannot select today's date or a future date. Please select a date at least 3 days before from today.");
      setFromDateTime(null);
      return;
    }

    if (toDateTime) {
      const selectedFromDate = dayjs(dateString).endOf("day");
      const selectedToDate = dayjs(toDateTime);

      if (
        !selectedFromDate.isSameOrBefore(
          selectedToDate.subtract(1, "day"),
          "day"
        )
      ) {
        message.warning("The from date must be at most 1 day before.");
        setFromDateTime(null);
        return;
      }
    }

    const tempDate = dayjs(dateString).format("YYYY-MM-DD HH:mm:ss");
    setFromDateTime(tempDate);
  }

  function onHandleToDate(date, dateString) {
    if (!date || !dateString) {
      setToDateTime(null);
      return;
    }
    if (date) {
      const tempToDate = date.format(`YYYY-MM-DD`);
      const twoDaysBefore = dayjs().subtract(2, "day").endOf("day");

      if (dayjs(tempToDate).isAfter(twoDaysBefore)) {
      message.warning(
        "You cannot select today's date or a future date. Please select a date at least 2 days before from today."
      );
      setToDateTime(null);
      return;
      }

      if (fromDateTime) {
      const fromDate = dayjs(fromDateTime).startOf("day");
      const toDate = dayjs(tempToDate).startOf("day");

      if (toDate.isSame(fromDate, "day")) {
        message.warning("From date and To date cannot be the same. To date must be at least 1 day after From date.");
        setToDateTime(null);
        return;
      }

      if (!toDate.isAfter(fromDate)) {
        message.warning("This date must be greater than the from date.");
        setToDateTime(null);
        return;
      }
      }

      let tempDate = tempToDate + " 23:59:59";
      tempDate = dayjs(tempDate).format("YYYY-MM-DD HH:mm:ss");
      setToDateTime(tempDate);
    } else {
      setToDateTime(null);
    }
  }


  function onHandlePageChange(page, pageSize) {
    setPage(page);
    setPerPage(pageSize);
  }

  function onHandlePageSizeChange(current, size) {
    setPerPage(size);
  }

  function onHandleArchived() {
    setArchiveLoading(true);
    const { page, perPage, ...params } = ordersFilter;
    const tempParams = {
      restaurantId: restaurantId,
      page: 1,
      perPage: 10,
      fromDateTime: "",
      toDateTime: ""
    };

    if (
      !Boolean(params.restaurantId) ||
      !Boolean(params.fromDateTime) ||
      !Boolean(params.toDateTime)
    ) {
      showNotification("Please select from and to date first!");
      setArchiveLoading(false);
      return;
    }

    if (ordersReportList.length < 1) {
      message.info('No data is available on these dates to archived.');
      onHandleClearFilter();
      fetchOrdersReportList(tempParams);
      setArchiveLoading(false);
      return;
    }

    ApiService.ORDER_ARCHIVED_WITH_DATE(params, user.token.token).then((res) => {
      if (res.code === 200) {
        showNotification("Order archive succefully.", false);
        onHandleClearFilter();
        fetchOrdersReportList(tempParams);
        setArchiveLoading(false);
        return;
      } else {
        showNotification(res.data.message);
        setArchiveLoading(false);
        return;
      }
    });
  }

    function onHandleClearFilter() {
    if (fromDateTime === "" && toDateTime === "") return;
    setIsFilterClear(true);
    setFromDateTime("");
    setToDateTime("");

    setOrdersFilter((prev) => ({
      restaurantId: prev.restaurantId,
      page: 1,
      perPage: 10,
      fromDateTime: "",
      toDateTime: ""
    }));
  }

  return (
    <section>
      <div className="flex flex-col">
        <Header title={""} menu={""} currentMenuKey={"T_6_S_6_L_5"} />
        <div className="flex flex-row bg-prewhite px-[12%]">
          <SideMenu title={`Sale Report`} menu={""} />
        </div>
        <CustomLoader />
        <div className=" mt-[80px] ml-[80px] px-[50px]">
          <h1 className="pt-[50px] pb-[20px] font-bold text-black text-[24px] leading-[24px] cursor-default">
            Orders Archived
          </h1>

          <OrderReportArchivedComponent
            reportData={ordersReportList}
            handleFilterApply={onHandleFilterData}
            handleArchived={onHandleArchived}
            page={page}
            perPage={perPage}
            setPerPage={setPerPage}
            total={totalItem}
            handlePageChange={onHandlePageChange}
            handlePageSizeChange={onHandlePageSizeChange}
            loading={networkCall}
            loadingArchive={archiveLoading}
            handleFromDate={onHandleFromDate}
            handleToDate={onHandleToDate}
            fromDateTime={fromDateTime}
            toDateTime={toDateTime}
            handleClearFilter={onHandleClearFilter}
          />
        </div>
      </div>
    </section>
  );
}
